<script lang="ts">

    /**
     * Component used for displaying a list of images
     * each image can be removed of the binded list of images
     * can add image to the binded list
     *
     * usage :
     * <ProductImageComponent bind:images={myImages}></ProductImageComponent>
     * with myImages a list of string. Each of its element can be a link to an image or a base64 image
     *
     * <ProductImageComponent bind:images={myImages} librarySearch={'logo'} bind:datasetAssetCorrespondance={datasetAssetCorrespondance}></ProductImageComponent>
     * if librarySearchis specified, you can load image from the logotheque. 'logo' loads the logos, 'picto' the pictos
     * datasetAssetCorrespondance is used to get the correspondance between datafiletuid and assetuid
     */

    import {onMount, getContext, createEventDispatcher} from "svelte";
    import Modal from "svelte-simple-modal";
    import LogothequeModal from "../modals/LogothequeModal.svelte";
    import {loader, modal, swClient, toast} from "../stores";
    import UrlPopup from "../modals/UrlPopup.svelte";
    import {YoutubeService} from "../services/YoutubeService";
    import {flip} from "svelte/animate";
    import {dndzone} from "svelte-dnd-action";

    const {open, close} = getContext('simple-modal');
    const dispatch = createEventDispatcher();

    /**
     * List of images : link to an image or a base64 image
     */
    export let images = [];
    /** The type of asset to load */
    export let librarySearch: 'picto' | 'logo' = null;
    /**Correspondance between datafileUid and AssetUid*/
    export let datasetAssetCorrespondance = {};
    export let type: 'image' | 'video' | 'document' = 'image'
    /** input file */
    let fileInput;
    /** id of input file */
    let inputId;
    /** is the component init ?*/
    let isInit = false;
    /** the content of assets corresponding of librarySearch*/
    let libraryContent: any[] = [];
    let acceptFile = "image/*"
    let items = [];

    const flipDurationMs = 300;
    const handleImgError = ev => ev.target.src = "/no_visual.jpg";

    // $: if (images) {
    //     console.log(images)
    //     items = []
    //     for (let i in images) {
    //         let item = {id: Number(i) + 1, img: images[i]}
    //         items = [...items, item]
    //     }
    // }

    onMount(async () => {
        // create unique id for input element
        inputId = Math.floor(Math.random() * 1000);
        isInit = true;
        switch (type) {
            case "image":
                acceptFile = "image/*"
                break
            case "document":
                acceptFile = "application/pdf"
                break

        }
        refresh()
    });

    export function refresh() {
        items = []
        for (let i in images) {
            let item = {id: Number(i) + 1, img: images[i]}
            items = [...items, item]
        }
    }

    function handleDndConsider(e) {
        items = e.detail.items;
    }

    function handleDndFinalize(e) {
        items = e.detail.items;
        let newImages = []
        for (let i in items) {
            let img = items[i].img
            newImages = [...newImages, img]
        }
        images = newImages
        dispatch('imgChanged', {
            'images': images
        });
    }

    const isValidUrl = urlString => {
        let url;
        try {
            url = new URL(urlString);
        } catch (e) {
            return false;
        }
        return url.protocol === "http:" || url.protocol === "https:";
    }

    function openUrlModal() {
        const onOkay = (value) => {
            if (isValidUrl(value)) {
                images.push(value);
                images = images;
                dispatch('imgChanged', {
                    'images': images
                });
                refresh()
            } else {
                $toast.error("Le lien n'est pas valide!")
            }
        }
        // open the modal
        open(
            UrlPopup,
            {
                onOkay,
                title: "Ajouter un lien vidéo"
            }
        );
    }

    /**
     * called when a file is upload in the inputfile element
     * Add a base64 image in the array of images
     */
    function onChangeFile() {
        const files = fileInput.files;
        // const file = files[0];
        for (let file of files) {
            if (file) {
                const reader = new FileReader();
                reader.addEventListener("load", function () {
                    if (type == "document") {
                        images.push({name: file.name, url: reader.result});
                    } else {
                        images.push(reader.result);
                    }
                    images = images;
                    fileInput.value = '';
                    dispatch('imgChanged', {
                        'images': images
                    });
                    refresh()
                });
                reader.readAsDataURL(file);
            }
        }
    }

    /**
     * remove the clicked image form the array
     */
    function removeImage(index: number) {
        images.splice(index, 1);
        images = images;
        dispatch('imgChanged', {
            'images': images
        });
        items = []
        for (let i in images) {
            let item = {id: Number(i) + 1, img: images[i]}
            items = [...items, item]
        }
    }

    /**
     * Open the logotheque modal
     * @param content : libraryContent
     * @param type : picto or logo
     */
    async function openLogothequeModal(content: any[], type: 'picto' | 'logo') {
        if (librarySearch && content.length === 0) {
            const processId = $loader.show("Chargement de la bibliothèque en cours, cela peut prendre quelques instants");
            const assets = await $swClient.get(`/v2/assets`);
            libraryContent = assets.filter(asset => {
                return asset.type === librarySearch;
            });
            content = libraryContent;
            $loader.hide(processId);
        }
        open(LogothequeModal, {
            libraryContent: content,
            type,
            onClose,
            images
        });
    }

    /**
     * Close the logotheque modal
     * @param datasetUid datafile uid
     * @param assetUid corresponding AssetUid
     */
    const onClose = (selectedImages: Array<{ datafileUid: string, assetUid: string }>) => {
        close(LogothequeModal);
        for (let i = 0; i < selectedImages.length; i++) {
            images.push(`${$swClient.uri}/v2/datafiles/${selectedImages[i].datafileUid}`);
            datasetAssetCorrespondance[selectedImages[i].datafileUid] = selectedImages[i].assetUid;
        }
        images = images;
    }

</script>


{#if isInit}
    {#if images}
        <div class="img-container" use:dndzone="{{items, flipDurationMs, type: inputId}}" on:consider="{handleDndConsider}" on:finalize="{handleDndFinalize}">
            {#each items as item, i (item.id)}
                <div class="img-zone" animate:flip="{{duration: flipDurationMs}}">
                    {#key items}
                    {#if type === 'image' && item.img}
                        <div class="document-icon">
                        <img class="img" on:error={handleImgError} src={item.img} alt="Preview"/>
                        </div>
                    {/if}
                    {#if type === 'video' && item.img}
                        <img
                            src={YoutubeService.getThumbailVideoUrl(item.img)}
                            alt="miniature de la vidéo"
                            style="height: 100%;"
                        />
                    {/if}
                    {#if type === 'document' && item.img}
                        <div class="document-icon">
                            <div><i class="fas fa-file-download fa-2x"></i></div>
                            <div>{item.img.name}</div>
                        </div>
                    {/if}
                    <button class="suppr-btn" on:click={() => removeImage(i)}>
                        <span title="Supprimer" class="fa fas fa-times"></span>
                    </button>
                    {/key}
                </div>
            {/each}
            <div class="img-zone">
                <div>
                    {#if type === 'image' || type === 'document'}
                        <input id={`inputId-${inputId}`} type="file" style="display:none;" accept="{acceptFile}" multiple
                               bind:this={fileInput} on:change={onChangeFile}/>
                        <button class="{librarySearch ? 'add-btn-left' : 'add-btn'}"
                                on:click={() => document.getElementById(`inputId-${inputId}`).click()}>
                            <span title="Ajouter depuis l'ordinateur local" class="fa fas fa-upload"></span>
                        </button>
                    {/if}
                    {#if type === 'video'}
                        <button class="add-btn"
                                on:click={() => openUrlModal()}>
                            <span title="Ajouter un lien de vidéo" class="fa fas fa-link"></span>
                        </button>
                    {/if}
                    {#if librarySearch}
                        <Modal show={$modal}>
                            <button class="add-btn-right"
                                    on:click={() => openLogothequeModal(libraryContent, librarySearch)}><span
                                    title="Ajouter depuis la logotheque" class="fa fas fa-book"></span></button>
                        </Modal>
                    {/if}
                </div>
            </div>
        </div>
    {/if}
{:else}
    <span>Chargement des images, veuillez patienter...</span>
{/if}

<style lang="scss">
  .img-container {
    width: 100%;
    min-height: 150px;
    min-width: 150px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);

    .img-zone {
      border: rgb(180, 180, 180);
      border-style: dotted;
      position: relative;
      float: left;
      margin-right: 10px;
      margin-bottom: 10px;
      width: auto;
      height: auto;
      max-width: 160px;
      max-height: 160px;

      .img {
        width: auto;
        height: auto;
        max-width: 150px;
        max-height: 150px;
      }

      .suppr-btn {
        position: absolute;
        top: 20%;
        left: 85%;
        transform: translate(-50%, -50%);
        -ms-transform: translate(-50%, -50%);
        background-color: rgba(200, 200, 200, 0.5);
        font-size: 16px;
        padding: 6px 12px;
        border: none;
        cursor: pointer;
        border-radius: 10px;
      }

      .suppr-btn:hover {
        background-color: rgba(128, 128, 128, 0.5);
      }

      .document-icon {
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }

      .add-btn {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        -ms-transform: translate(-50%, -50%);
        background-color: rgba(200, 200, 200, 0.5);
        font-size: 16px;
        padding: 12px 24px;
        border: black;
        cursor: pointer;
        border-radius: 10px;
      }

      .add-btn-left {
        position: absolute;
        top: 50%;
        left: 25%;
        transform: translate(-50%, -50%);
        -ms-transform: translate(-50%, -50%);
        background-color: rgba(200, 200, 200, 0.5);
        font-size: 16px;
        padding: 12px 24px;
        border: black;
        cursor: pointer;
        border-radius: 10px;
      }

      .add-btn-right {
        position: absolute;
        top: 50%;
        left: 75%;
        transform: translate(-50%, -50%);
        -ms-transform: translate(-50%, -50%);
        background-color: rgba(200, 200, 200, 0.5);
        font-size: 16px;
        padding: 12px 24px;
        border: black;
        cursor: pointer;
        border-radius: 10px;
      }

      .add-btn:hover, .add-btn-left:hover, .add-btn-right:hover {
        background-color: rgba(128, 128, 128, 0.5);
      }
    }
  }
</style>
